import { Button } from '@heroui/react';
import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import useSegment from '@/hooks/useSegment';

import { EntryList } from '../EntryList';

import { LineChart } from './LineChart';

const EVENT = 'Progress chart date changed';

export function DailyLineData({ question }) {
  const { t } = useTranslation();
  const { sendTrackEvent } = useSegment();
  const { pathname } = useLocation();

  const [startOfDay, setStartOfDay] = useState(dayjs().startOf('day'));

  const trackNavigation = (direction, date) => {
    const eventData = {
      id: question.id,
      direction,
      pathname,
      procedureId: question.procedureId,
      procedureName: question.surveyName,
      questionType: question.type,
      startDate: dayjs(date).format('YYYY-MM-DD').toString(),
      timeFrame: 'day',
      title: question.title,
    };

    sendTrackEvent(EVENT, eventData);
  };

  const getPrevDay = () => {
    const prevDay = dayjs(startOfDay).subtract(1, 'days');
    setStartOfDay(prevDay);
    trackNavigation('back', prevDay);
  };

  const disableBack = useMemo(() => {
    const { patientEntries } = question;
    const firstEntryDate = _.first(patientEntries).date;
    return dayjs(firstEntryDate).isSameOrAfter(startOfDay, 'day');
  }, [startOfDay, question]);

  const getNextDay = () => {
    const nextDay = dayjs(startOfDay).add(1, 'days');
    setStartOfDay(nextDay);
    trackNavigation('next', nextDay);
  };

  const disableNext = useMemo(
    () => dayjs().isSame(startOfDay, 'day'),
    [startOfDay]
  );

  const entriesForDay = useMemo(() => {
    const { patientEntries } = question;
    const data = _.filter(patientEntries, ({ date }) =>
      dayjs(date).isSame(startOfDay, 'day')
    );

    if (_.isEmpty(data)) {
      data.push({
        date: startOfDay,
        answer: 0,
        skipped: true,
      });
    }

    return data;
  }, [startOfDay, question]);

  return (
    <>
      <h3 className="text-sm">{dayjs(startOfDay).format('LL')}</h3>
      <div className="flex flex-row">
        <Button
          color="primary"
          isDisabled={disableBack}
          variant="light"
          onPress={getPrevDay}
        >
          <Icon icon="mdi:chevron-left" />
          {t('common.action.back')}
        </Button>

        <Button
          color="primary"
          isDisabled={disableNext}
          variant="light"
          onPress={getNextDay}
        >
          {t('common.action.next')}
          <Icon icon="mdi:chevron-right" />
        </Button>
      </div>
      <LineChart
        question={question}
        filteredEntries={entriesForDay}
        mode="day"
      />
      {!question.metricTracker && (
        <EntryList
          question={question}
          filteredEntries={entriesForDay}
          mode="day"
        />
      )}
    </>
  );
}
